<template>
  <b-modal
    ref="modal"
    :title="$t('widgetAreas.advanceSettings')"
    centered
    hide-footer
  >
    <div v-if="initializing" class="text-center">
      <font-awesome-icon spin icon="circle-notch" class="text-moremuted display-2"/>
    </div>

    <template v-else>
      <b-form-group>
        <currency-editor :prepend="$t('products.minPrice')" input-class="text-center" v-model="model.minPrice"/>
      </b-form-group>

      <b-form-group>
        <currency-editor :prepend="$t('products.maxPrice')" input-class="text-center" v-model="model.maxPrice"/>
      </b-form-group>

      <b-form-group>
        <b-input-group :prepend="$t('widgetAreas.keyword')">
          <b-form-input dir="auto" v-model="model.keyword"/>
        </b-input-group>
      </b-form-group>

      <b-form-group>
        <b-input-group :prepend="$t('shared.tags')">
          <b-form-tags
            :placeholder="`${$t('shared.add')}...`"
            :add-button-text="$t('shared.add')"
            add-button-variant="light"
            tag-variant="warning"
            tag-pills
            :tag-remove-label="$t('shared.remove')"
            :duplicate-tag-text="$t('shared.duplicateItem', { title: this.$t('shared.tag') })"
            :invalid-tag-text="$t('shared.invalidItem', { title: this.$t('shared.tag') })"
            separator=",،"
            remove-on-delete
            v-model="model.tags"
          />
        </b-input-group>
      </b-form-group>

      <b-form-group>
        <b-input-group :prepend="$t('shared.sortOrder')">
          <b-form-select :options="sortOptions" v-model="model.sort"/>
        </b-input-group>
      </b-form-group>

      <b-form-group>
        <auto-complete-multi
          :placeholder="$t('dealers.enterDealer')"
          :prepend="$t('nav.dealers')"
          :search="dealersSearch"
          :select="dealersSelect"
          v-model="model.sellerIds"
        />
      </b-form-group>

      <b-form-group>
        <auto-complete-multi
          :placeholder="$t('products.enterBrand')"
          :prepend="$t('nav.brands')"
          :search="brandsSearch"
          :select="brandsSelect"
          v-model="model.brandIds"
        />
      </b-form-group>

      <b-form-group v-if="model.brandIds && model.brandIds.length">
        <auto-complete
          :placeholder="$t('widgetAreas.enterType')"
          :prepend="$t('widgetAreas.type')"
          :search="typeSearch"
          :select="typeSelect"
          v-model="model.typeId"
        />
      </b-form-group>

      <template v-if="model.typeId">
        <b-form-group v-if="sizeOptions && sizeOptions.length > 1" :label="`${$t('nav.sizes')}:`">
          <b-form-checkbox-group
            stacked
            :options="sizeOptions"
            class="bg-light rounded p-3"
            v-model="model.sizeIds"
          />
        </b-form-group>

        <b-form-group v-if="colorOptions && colorOptions.length > 1" :label="`${$t('nav.colors')}:`">
          <b-form-checkbox-group
            stacked
            :options="colorOptions"
            class="bg-light rounded p-3"
            v-model="model.colorIds"
          />
        </b-form-group>
      </template>

      <b-form-group :class="{'mb-0': !groups.length}">
        <auto-complete-multi
          :placeholder="$t('widgetAreas.enterGroup')"
          :prepend="$t('nav.groups')"
          :search="groupsSearch"
          :select="groupsSelect"
          v-model="model.groupIds"
        />
      </b-form-group>

      <b-tabs v-if="groups && groups.length" class="tabs-light">
        <b-tab v-for="item in groups" :key="item.id" :title="item.name">
          <categories
            v-if="item.categories && item.categories.length"
            :items="item.categories"
            v-model="model.categoryIds"
          />

          <div v-else class="text-muted text-center p-4">
            <font-awesome-icon icon="times-circle" class="display-3 d-block mx-auto text-moremuted mb-4"/>

            <p class="mb-0">{{ $t('productCategories.noCategoryInThisGroup') }}</p>
          </div>
        </b-tab>
      </b-tabs>
    </template>
  </b-modal>
</template>

<script>
import Settings from '@/mixins/settings/index'
import { AutoComplete, AutoCompleteMulti, Categories, CurrencyEditor } from '@/components/shared'

export default {
  name: 'AdvanceSettings',
  mixins: [Settings],
  components: {
    AutoComplete,
    AutoCompleteMulti,
    Categories,
    CurrencyEditor
  },
  props: {
    value: Object
  },
  data () {
    return {
      initializing: true,
      groups: [],
      type: null,
      sortOptions: [
        { value: 0, text: this.$t('widgetAreas.sortOptions.newest') },
        { value: 1, text: this.$t('widgetAreas.sortOptions.mostLikes') },
        { value: 2, text: this.$t('widgetAreas.sortOptions.mostHits') },
        { value: 3, text: this.$t('widgetAreas.sortOptions.mostSalesCount') },
        { value: 4, text: this.$t('widgetAreas.sortOptions.mostRate') },
        { value: 5, text: this.$t('widgetAreas.sortOptions.cheapest') },
        { value: 6, text: this.$t('widgetAreas.sortOptions.mostExpensive') },
        { value: 7, text: this.$t('widgetAreas.sortOptions.mostDiscount') }
      ]
    }
  },
  mounted () {
    this.getSettings('currency')
    .then(() => {
      this.initializing = false
    })
  },
  watch: {
    'model.brandIds' (val, oldVal) {
      if (val !== oldVal) {
        this.model.typeId = null
        this.model.sizeIds = []
        this.model.colorIds = []
        this.model.groupIds = []
        this.groups = []
        this.model.categoryIds = []
      }
    },
    'model.typeId' (val, oldVal) {
      if (val && val !== oldVal) {
        this.model.sizeIds = []
        this.model.colorIds = []
        this.model.groupIds = []
        this.groups = []
        this.model.categoryIds = []
        this.getType(val)
      }
    },
    'model.groupIds' (val, oldVal) {
      if (val !== oldVal) {
        this.groups = []
        this.model.categoryIds = []

        if (this.model.typeId && this.model.groupIds && this.model.groupIds.length) {
          this.getGroups()
          .then(() => {
            if (this.groups && this.groups.length) {
              this.groups.forEach(item => {
                this.getCategories(item.id)
                .then(res => {
                  item.categories = res
                })
              })
            }
          })
        }
      }
    }
  },
  computed: {
    model: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    sizeOptions () {
      if (this.type) {
        const items = this.type.sizes && this.type.sizes.length ? this.type.sizes : null
        return items ? [].concat(items.map(o => {
          return {
            value: o.id,
            text: o.name
          }
        })) : null
      }
    },
    colorOptions () {
      if (this.type) {
        const items = this.type.colors && this.type.colors.length ? this.type.colors : null
        return items ? [].concat(items.map(o => {
          return {
            value: o.id,
            text: o.name
          }
        })) : null
      }
    }
  },
  methods: {
    show () {
      if (this.model.typeId && !this.type) this.getType(this.model.typeId)
      if (this.model.typeId && this.model.groupIds && this.model.groupIds.length) {
        this.getGroups()
        .then(() => {
          if (this.groups && this.groups.length) {
            this.groups.forEach(item => {
              this.getCategories(item.id)
              .then(res => {
                item.categories = res
              })
            })
          }
        })
      }
      this.$refs.modal.show()
    },
    getType (id) {
      return this.$api.productTypes.getById(id)
      .then(res => {
        this.type = res
      })
      .catch(err => {
        this.$showError(this.$getLocaleErrorMessage(err, 'productTypes'))
      })
    },
    getGroups () {
      return this.$api.productGroups.getInfos({ ids: this.model.groupIds, sort : 'sortOrder-asc' })
      .then(res => {
        return this.groups = res.map(i => {
          i.categories = []
          return i
        })
      })
      .catch(this.fail)
    },
    getCategories (groupId) {
      return this.$api.productCategories.getInfos({
        productGroupId: groupId,
        productTypeId: this.model.typeId
      })
      .then(res => {
        return res
      })
      .catch(this.fail)
    },
    dealersSearch (keyword, count) {
      return this.$api.dealers.getInfos({
        dealerFullName: keyword,
        limit: count,
        activeSellersOnly: true
      })
      .then(items => {
        return items.map(item => {
          return {
            text: this.getUserDisplayName(item),
            value: item.id
          }
        })
      })
    },
    dealersSelect (ids) {
      return this.$api.dealers.getInfos({ ids })
      .then(items => items.map(item => {
        return {
          text: this.getUserDisplayName(item),
          value: item.id
        }
      }))
    },
    brandsSearch (keyword, count) {
      return this.$api.productBrands.getInfos({
        name: keyword,
        limit: count
      })
      .then(items => items.map(item => {
        return {
          text: item.name,
          value: item.id
        }
      }))
    },
    brandsSelect (ids) {
      return this.$api.productBrands.getInfos({ ids })
      .then(items => items.map(item => {
        return {
          text: item.name,
          value: item.id
        }
      }))
    },
    typeSearch (keyword, count) {
      return this.$api.productTypes.getInfos({
        title: keyword,
        limit: count,
        brandIds: this.model.brandIds
      })
      .then(items => items.map(item => {
        return {
          text: item.title,
          value: item.id
        }
      }))
    },
    typeSelect (value) {
      return this.$api.productTypes.getInfos({
        id: value
      })
      .then(items => {
        return {
          text: items[0].title,
          value: items[0].id
        }
      })
    },
    groupsSearch (keyword, count) {
      return this.$api.productGroups.getInfos({
        name: keyword,
        limit: count
      })
      .then(items => {
        return items.map(item => {
          return {
            text: item.name,
            value: item.id
          }
        })
      })
    },
    groupsSelect (ids) {
      return this.$api.productGroups.getInfos({ ids })
      .then(items => items.map(item => {
        return {
          text: item.name,
          value: item.id
        }
      }))
    },
    getUserDisplayName (user) {
      return this.$api.account.getUserDisplayName(user)
    }
  }
}
</script>
